import React from 'react';

import Haw1 from 'Assets/landing-page/haw1.png';
import Haw2 from 'Assets/landing-page/haw2.png';
import SecondaryCTA from 'Assets/landing-page/secondary-cta.png';
import ULPHero from 'Assets/landing-page/ulp-hero.png';
import { RichTextContent } from 'ContentfulDefaults/types/_shared';
import { ContentfulTextNote } from 'ContentfulWrappers/ContentfulTextNote';
import { useTextContext } from 'Contexts/textContext';
import { H1 } from 'DesignLibrary/atoms';
import { TextNote } from 'DesignLibrary/molecules/TextNote';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { getEnvironmentFromUrl } from 'Utils/urls';

import GetStartedButton from './GetStartedButton';
import { HeroContainer, HeroContent, HeroContentWrapper } from './styled';
import { DEPLOY_ENVIRONMENTS } from '../../../constants';
import MarketingCarousel from '../MarketingCarousel';

export interface HeroProps {
  isNextClicked: boolean;
  isLoading: boolean;
  isAlexIdEnabled: boolean;
  forceAlexID: boolean;
  handleNext: (e: React.MouseEvent<HTMLButtonElement>) => void;
  getStartedButtonRef: any;
  getStartedButtonAccessibilityProps: any;
}

export const Hero2025 = ({
  isNextClicked,
  isLoading,
  isAlexIdEnabled,
  forceAlexID,
  handleNext,
  getStartedButtonRef = null,
  getStartedButtonAccessibilityProps = {},
}: HeroProps) => {
  const { retrieveContentfulData } = useTextContext();

  const alertBannerEnabled = retrieveContentfulData<boolean>(
    'unified_landing_page.alert_banner_enabled',
    false,
  );
  const alertBannerContent = retrieveContentfulData<RichTextContent>(
    'unified_landing_page.alert_banner_content',
  );

  return (
    <HeroContainer className="hero-2025" isNextClicked={isNextClicked}>
      <HeroContentWrapper isNextClicked={isNextClicked}>
        <HeroContent isNextClicked={isNextClicked} className="content" data-testid="hero-content">
          <H1 as="h1" weight="normal">
            <Text field="unified_landing_page.hero.title_primary" />{' '}
            <span>
              <Text field="unified_landing_page.hero.title_trailing_emphasis" />
            </span>
          </H1>
          <p>
            <Text field="unified_landing_page.hero.subheader" />
          </p>
          {isAlexIdEnabled && getEnvironmentFromUrl() === DEPLOY_ENVIRONMENTS.UAT && (
            <p className="alex-id-warning">
              <h3 className="cta-message">
                <Text field="unified_landing_page.hero.uat_no_login_header" />
              </h3>
              <Text field="unified_landing_page.hero.uat_no_login_message" />
            </p>
          )}

          <div className="cta-buttons">
            <GetStartedButton
              getStartedButtonRef={getStartedButtonRef}
              isAlexIdEnabled={isAlexIdEnabled}
              isLoading={isLoading}
              handleNext={handleNext}
              forceAlexID={forceAlexID}
              getStartedButtonAccessibilityProps={getStartedButtonAccessibilityProps}
            />
          </div>
          <ContentfulTextNote className="hero-text-note" sectionKey="unified_landing_page" />
          {alertBannerEnabled && alertBannerContent && (
            <TextNote
              className="alert-banner-text-note"
              data-testid="alert-banner-text-note"
              mb={2}
              shortDisplayText={
                <RichText inlineParagraphs={false} field={undefined} fieldContent={alertBannerContent} />
              }
            />
          )}
        </HeroContent>
        <div className="animation">
          <img src={ULPHero} alt="eyeball man picking a leaf" />
        </div>
      </HeroContentWrapper>
      <MarketingCarousel />
      <div className="rounded-container">
        <h2>
          <Text field="unified_landing_page.hero.alex_explanation_header" />
        </h2>
        <div className="haw">
          <div>
            <img src={Haw1} alt="profile question images" />
            <p>
              <Text field="unified_landing_page.hero.alex_profile_explanation" />
            </p>
          </div>
          <div>
            <img src={Haw2} alt="plan selection options" />
            <p>
              <Text field="unified_landing_page.hero.alex_plan_selection_explanation" />
            </p>
          </div>
        </div>
      </div>
      <div className="secondary-CTA">
        <div>
          <img src={SecondaryCTA} alt="a person on a laptop" />
        </div>
        <div className="text">
          <h2>
            <Text field="unified_landing_page.hero.secondary_cta_header_primary" />{' '}
            <span>
              <Text field="unified_landing_page.hero.secondary_cta_header_trailing_emphasis" />
            </span>
            <Text field="unified_landing_page.hero.secondary_cta_header_final_punctuation" />
          </h2>
          <p>
            <Text field="unified_landing_page.hero.secondary_cta_message" />
          </p>
          <div className="cta-buttons">
            <GetStartedButton
              getStartedButtonRef={getStartedButtonRef}
              isAlexIdEnabled={isAlexIdEnabled}
              isLoading={isLoading}
              handleNext={handleNext}
              forceAlexID={forceAlexID}
              getStartedButtonAccessibilityProps={getStartedButtonAccessibilityProps}
            />
          </div>
        </div>
      </div>
    </HeroContainer>
  );
};
